<template>
  <div v-if="buyersLoading">
    <loader :height="400" />
  </div>
  <form v-else @submit.prevent="submitRemittance" novalidate>
    <div class="drip-h3 font-weight-400 color-noble-blue-500">
      {{ $t('remittances.addRemittance.inputs[0].heading') }}
    </div>
    <div class="spacing-12"></div>
    <!-- Buyers -->
    <div class="input-container">
      <div class="input-label">
        {{ $t('remittances.addRemittance.inputs[0].label') }}
      </div>
      <div class="input-block">
        <multiselect
          id="buyers"
          name="buyers"
          data-vv-as="Buyers"
          :placeholder="$t('remittances.addRemittance.inputs[0].placeHolder')"
          v-model="buyersValue"
          v-validate="'multiple_required'"
          class="drip-dropdown"
          label="importer"
          :options="buyersOptions"
          :show-labels="false"
          selectLabel=""
          selectedLabel=""
          deselectLabel=""
          @open="isOpen = true"
          @close="isOpen = false"
        >
          <template slot="option" slot-scope="{ option }">
            {{ option.importer }}
          </template>
          <template slot="caret">
            <img
              v-if="isOpen"
              src="@/assets/images/icons/chevron-down.svg"
              alt="caret"
              class="action-input"
            />
            <img
              v-else
              src="@/assets/images/icons/chevron-down.svg"
              alt="caret"
              class="action-input inverse"
            />
          </template>
        </multiselect>
        <span v-if="errors.has('buyers')" class="error">
          {{ errors.first('buyers') }}
        </span>
      </div>
    </div>
    <!-- Remitter/Third Party/Consignee Name: -->
    <div class="input-container mb-2">
      <div class="input-label">
        {{ $t('remittances.addRemittance.inputs[1].label') }}
      </div>
      <div class="input-block">
        <input
          id="remitter_name"
          name="remitter_name"
          data-vv-as="Remitter/Third Party/Consignee Name"
          type="text"
          v-model="remitterName"
          :placeholder="$t('remittances.addRemittance.inputs[1].placeHolder')"
        />
        <span v-if="errors.has('remitter_name')" class="error">
          {{ errors.first('remitter_name') }}
        </span>
      </div>
    </div>
    <div class="input-container checkbox-1 mt-1 mb-3">
      <b-form-checkbox
        id="checkbox-1"
        name="checkbox-1"
        v-model="isSameName"
        value="true"
        unchecked-value="false"
        class="text-align-center font-weight-bold"
      >
        <div class="cursor-pointer font-weight-400">
          {{ $t('remittances.addRemittance.inputs[1].checkboxText') }}
        </div>
      </b-form-checkbox>
    </div>
    <!-- Remittance Currency -->
    <div class="input-container">
      <div class="input-label">
        {{ $t('remittances.addRemittance.inputs[2].label') }}
      </div>
      <div class="input-block width-800">
        <multiselect
          id="remittance_currency"
          name="remittance_currency"
          data-vv-as="Remittance Currency"
          :placeholder="$t('remittances.addRemittance.inputs[2].placeHolder')"
          v-model="currencyValue"
          v-validate="'multiple_required'"
          class="drip-dropdown"
          :options="currencyOptions"
          :show-labels="false"
          selectLabel=""
          selectedLabel=""
          deselectLabel=""
          @open="isOpen = true"
          @close="isOpen = false"
        >
          <template slot="option" slot-scope="{ option }">
            {{ option }}
          </template>
          <template slot="caret">
            <img
              v-if="isOpen"
              src="@/assets/images/icons/chevron-down.svg"
              alt="caret"
              class="action-input"
            />
            <img
              v-else
              src="@/assets/images/icons/chevron-down.svg"
              alt="caret"
              class="action-input inverse"
            />
          </template>
        </multiselect>
        <span v-if="errors.has('remittance_currency')" class="error">
          {{ errors.first('remittance_currency') }}
        </span>
      </div>
    </div>
    <!-- Remittance Amount -->
    <div class="input-container">
      <div class="input-label">
        {{ $t('remittances.addRemittance.inputs[3].label') }}
      </div>
      <div class="input-block position-relative width-800">
        <div class="prepend-item">
          {{ (currencyValue && getCurrencySymbol(currencyValue)) || '-' }}
        </div>
        <input
          id="remittance_amount"
          name="remittance_amount"
          data-vv-as="Remittance Amount"
          v-currency-mask
          type="text"
          class="prepend-icon"
          :placeholder="$t('remittances.addRemittance.inputs[3].placeHolder')"
          v-validate="'required||numbers_only'"
        />
        <span v-if="errors.has('remittance_amount')" class="error">
          {{ errors.first('remittance_amount') }}
        </span>
      </div>
    </div>

    <!-- Invoices -->
    <div class="drip-h3 font-weight-400 color-noble-blue-500">
      {{ $t('remittances.addRemittance.inputs[4].heading') }}
    </div>
    <div class="spacing-2"></div>
    <div class="drip-body font-weight-400">
      {{ $t('remittances.addRemittance.inputs[4].subtext') }}
    </div>
    <div class="spacing-8"></div>

    <div class="input-container">
      <div class="input-label">
        {{ $t('remittances.addRemittance.inputs[4].label') }}
      </div>
      <div class="input-block width-800">
        <multiselect
          id="invoices"
          name="invoices"
          :multiple="true"
          data-vv-as="Invoices"
          :placeholder="$t('remittances.addRemittance.inputs[4].placeHolder')"
          :preserve-search="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preselect-first="true"
          label="reference"
          track-by="id"
          v-model="invoiceValue"
          v-validate="'multiple_required'"
          class="drip-dropdown"
          :options="invoiceOptions"
          :show-labels="false"
          selectLabel=""
          selectedLabel=""
          deselectLabel=""
          @open="invoiceDropdownOpened()"
          @close="isOpen = false"
        >
          <template #selection="{ values, isOpen }">
            <span
              class="multiselect__single"
              v-if="values.length"
              v-show="!isOpen"
            >
              {{ getSelectedText(values) }}
            </span>
          </template>
          <template slot="option" slot-scope="props">
            <div class="option__desc d-flex align-items-center">
              <div
                v-if="
                  invoiceValue &&
                  invoiceValue.map((x) => x.id).includes(props.option.id)
                "
                class="mr-2"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="24" height="24" rx="4" fill="#26B67C" />
                  <g clip-path="url(#clip0_6324_1720)">
                    <path
                      d="M9.00016 16.1698L4.83016 11.9998L3.41016 13.4098L9.00016 18.9998L21.0002 6.99984L19.5902 5.58984L9.00016 16.1698Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_6324_1720">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
              <div class="mr-2" v-else>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="1"
                    y="1"
                    width="22"
                    height="22"
                    rx="3"
                    fill="white"
                    stroke="#26B67F"
                    stroke-width="2"
                  />
                </svg>
              </div>
              <div>
                {{ props.option.reference }}
              </div>
            </div>
          </template>
          <template slot="caret">
            <img
              v-if="isOpen"
              src="@/assets/images/icons/chevron-down.svg"
              alt="caret"
              class="action-input"
            />
            <img
              v-else
              src="@/assets/images/icons/chevron-down.svg"
              alt="caret"
              class="action-input inverse"
            />
          </template>
        </multiselect>
        <span v-if="invoiceListError" class="error">
          {{ invoiceListError }}
        </span>
        <span v-else-if="errors.has('invoices')" class="error">
          {{ errors.first('invoices') }}
        </span>
      </div>
    </div>

    <template v-if="isUnFinancedSelected">
      <div class="drip-h3 font-weight-400 color-noble-blue-500">
        {{ $t('remittances.addRemittance.inputs[5].heading') }}
      </div>
      <div class="spacing-8"></div>
      <!-- Bank -->
      <div class="input-container">
        <div class="input-label">
          {{ $t('remittances.addRemittance.inputs[5].placeHolder') }}
        </div>
        <div class="input-block width-800">
          <multiselect
            id="bank"
            name="bank"
            data-vv-as="Bank"
            :placeholder="$t('remittances.addRemittance.inputs[5].placeHolder')"
            v-model="bankValue"
            v-validate="'multiple_required'"
            class="drip-dropdown"
            :options="bankOptions"
            :show-labels="false"
            label="cname"
            selectLabel=""
            selectedLabel=""
            deselectLabel=""
            @open="isOpen = true"
            @close="isOpen = false"
          >
            <template slot="option" slot-scope="{ option }">
              {{ option.cname }}
            </template>
            <template slot="caret">
              <img
                v-if="isOpen"
                src="@/assets/images/icons/chevron-down.svg"
                alt="caret"
                class="action-input"
              />
              <img
                v-else
                src="@/assets/images/icons/chevron-down.svg"
                alt="caret"
                class="action-input inverse"
              />
            </template>
          </multiselect>
          <span v-if="errors.has('bank')" class="error">
            {{ errors.first('bank') }}
          </span>
        </div>
      </div>

      <!-- Amount -->
      <div class="input-container">
        <div class="input-label">
          {{ $t('remittances.addRemittance.inputs[6].label') }}
        </div>
        <div class="input-block position-relative width-800">
          <div class="prepend-item">
            {{ (currencyValue && getCurrencySymbol(currencyValue)) || '-' }}
          </div>
          <input
            id="amount"
            ref="amount"
            name="amount"
            data-vv-as="Remittance Amount (Un-financed)"
            v-currency-mask
            type="text"
            class="prepend-icon"
            :placeholder="$t('remittances.addRemittance.inputs[6].placeHolder')"
            v-validate="'required|numbers_only'"
          />
          <span v-if="errors.has('amount')" class="error">
            {{ errors.first('amount') }}
          </span>
        </div>
      </div>

      <!-- Narration -->
      <div class="input-container">
        <div class="input-label">
          {{ $t('remittances.addRemittance.inputs[7].label') }}
        </div>
        <div class="input-block">
          <input
            id="narration"
            ref="narration"
            name="narration"
            data-vv-as="Narration"
            type="text"
            class="w-100"
            :placeholder="$t('remittances.addRemittance.inputs[7].placeHolder')"
            v-validate="'required'"
          />
          <span v-if="errors.has('narration')" class="error">
            {{ errors.first('narration') }}
          </span>
        </div>
      </div>
    </template>
    <div class="spacing-16"></div>
    <div class="input-container color-noble-blue-500">
      <div class="drip-h3 font-weight-400">
        {{ $t('remittances.addRemittance.uploads.title') }}
      </div>
      <div class="spacing-12"></div>
      <div class="supporting-document">
        <uploads
          :index="0"
          :title="$t('remittances.addRemittance.uploads.cardTitle')"
          :subtext="$t('remittances.addRemittance.uploads.cardSubtitle')"
          itemKey="remittance_supporting_document"
          docTag="remittance_supporting_document"
          :uploadsSaved="uploadsSaved"
          :isOptional="false"
          :uploadBorder="true"
          acceptedFiles=""
          :multiple="['remittance_supporting_document']"
          :apiUrl="apiUrl"
          :maxFilesize="10"
        />
        <div class="error" v-if="showErrors">
          {{ $t('remittances.addRemittance.uploads.errorMessage') }}
        </div>
      </div>
    </div>
    <div class="spacing-16"></div>
    <!-- Additional instructions regarding the remittance -->
    <div class="input-container">
      <div class="input-label font-16 font-weight-bold">
        {{ $t('remittances.addRemittance.inputs[8].label') }}
      </div>
      <div class="input-block width-800">
        <textarea
          id="instructions"
          name="instructions"
          data-vv-as="Instructions"
          type="text"
          rows="2"
          :placeholder="$t('remittances.addRemittance.inputs[8].placeHolder')"
        ></textarea>
        <span v-if="errors.has('instructions')" class="error">
          {{ errors.first('instructions') }}
        </span>
      </div>
    </div>
    <div class="spacing-16"></div>
    <action-button variant="solid" type="submit">
      <b-icon-arrow-repeat animation="spin" class="mr-2" v-if="submitLoading" />
      {{ $t('remittances.addRemittance.submitButton') }}
      <img
        src="@/assets/images/common/check-mark.svg"
        class="ml-2"
        v-if="!submitLoading"
      />
    </action-button>
  </form>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { required } from 'vee-validate/dist/rules.esm'
import { CURRENCY_MAP, CURRENCY_SYMBOL } from '@/utils/constants'
import ActionButton from '@/components/common/ActionButton'
import Uploads from '@/components/common/Uploads'
import Loader from '@/components/common/Loader'
import remittanceService from '@/services/remittance'
import { uploadFilesBus } from '../../main'

export default {
  props: ['submitLoading', 'uploadsSaved'],
  components: {
    ActionButton,
    Uploads,
    Multiselect,
    Loader
  },
  data() {
    return {
      isSameName: '',
      remitterName: '',
      currencyValue: null,
      buyersValue: null,
      invoiceValue: null,
      bankValue: '',
      invoiceOptions: [],
      bankOptions: [],
      buyersOptions: [],
      currencyOptions: Object.keys(CURRENCY_MAP),
      apiUrl: `${process.env.VUE_APP_DRIP_API_HOST}/v1/onboarding/leads/${this.$store.getters.accountId}/documents`,
      isOpen: false,
      invoiceListError: '',
      buyersLoading: true
    }
  },
  created() {
    this.$validator.extend('remove_specials', {
      ...required,
      validate(value) {
        return /^[a-zA-Z0-9\s]*$/.test(value)
      }
    })
    this.$validator.extend('remove_number', {
      ...required,
      validate(value) {
        return !/[0-9]/.test(value)
      }
    })
    this.$validator.extend('numbers_only', {
      ...required,
      validate(value) {
        return /^[0-9,]*(\.[0-9]+)?$/.test(value)
      }
    })
    this.$validator.extend(
      'multiple_required',
      (value) => {
        let valid = false
        if (Array.isArray(value) && value.length) {
          valid = true
        } else if (value && Object.keys(value).length) {
          valid = true
        }
        return {
          valid,
          data: {
            required: true
          }
        }
      },
      {
        computesRequired: true
      }
    )
  },
  mounted() {
    this.getBuyerList()
  },
  computed: {
    showErrors() {
      return this.uploadsSaved[0] && this.uploadsSaved[0].errorMessage
    },
    isUnFinancedSelected() {
      if (
        this.invoiceValue &&
        this.invoiceValue.length &&
        this.invoiceValue.map((x) => x.id).includes(0)
      ) {
        return true
      }
      return false
    }
  },
  methods: {
    getCurrencySymbol(curr) {
      if (curr) {
        return CURRENCY_SYMBOL[curr] || ''
      }
      return ''
    },
    invoiceDropdownOpened() {
      this.invoiceListError = ''
      if (!this.buyersValue && !this.currencyValue) {
        this.invoiceListError = 'The buyers and currency field is not selected'
        return
      }
      if (!this.buyersValue) {
        this.invoiceListError = 'The buyers field is not selected'
        return
      }
      if (!this.currencyValue) {
        this.invoiceListError = 'The currency field is not selected'
      }
      this.isOpen = true
    },
    getSelectedText(values) {
      let text = ''
      if (values.length === 1 && values[0].id === 0) {
        return 'Unfinanced'
      }
      if (values) {
        text += values.filter((x) => x.id !== 0).length + ' selected '
      }
      if (values.map((x) => x.id).includes(0)) {
        text += '+ Unfinanced'
      }
      return text
    },
    getBuyerList() {
      remittanceService
        .fetchBuyerList(this.$store.state.account.accountId)
        .then((res) => {
          this.buyersOptions = res.data.clients_locked
            .map((c) => ({
              id: c.importer_user_id,
              importer: c.importer
            }))
            .sort((a, b) => {
              return a.importer.localeCompare(b.importer, 'en', {
                sensitivity: 'base'
              })
            })
        })
        .catch((e) => {
          console.log(e)
        })
        .finally(() => {
          this.buyersLoading = false
        })
    },
    hidePartialAccountNumber(accNumber) {
      return '******' + accNumber.slice(accNumber.length - 4, accNumber.length)
    },
    getBanksList(curr) {
      remittanceService
        .fetchBankList(
          this.$store.state.account.accountId,
          CURRENCY_MAP[curr],
          'verified'
        )
        .then((res) => {
          const bankList = [...res.data.Verified]
          this.bankOptions = bankList.map((c) => ({
            id: c.id,
            name: c.name,
            cname: `${c.name} | ${this.hidePartialAccountNumber(c.account)}`
          }))
        })
        .catch((e) => {
          console.log(e)
        })
    },
    getInvoiceList(importerId, currencyValue) {
      if (importerId && currencyValue) {
        remittanceService
          .fetchInvoiceByImporterId(
            this.$store.state.account.accountId,
            CURRENCY_MAP[this.currencyValue],
            importerId
          )
          .then((res) => {
            const options = res.data['drip/business/invoices'].map((c) => ({
              id: c.id,
              reference: c.reference
            }))
            this.invoiceValue = null
            this.invoiceOptions = []
            this.invoiceOptions.push({
              id: 0,
              reference: 'Un-financed Invoice'
            })
            if (options && options.length) {
              this.invoiceOptions.push(...options)
            }
          })
          .catch((e) => {
            console.log(e)
          })
      }
    },
    scrollToErrors() {
      if (document.querySelector('.error')) {
        document.querySelector('.error').scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }
      return document.querySelector('.error')
    },
    convertToNumber(num) {
      if (num) {
        return num.split(',').join('')
      }
      return num
    },
    submitRemittance(e) {
      this.$validator.validate()
      if (
        this.uploadsSaved[0] &&
        !this.uploadsSaved[0].remittance_supporting_document.length
      ) {
        uploadFilesBus.$emit('documentRequired', true)
        this.scrollToErrors()
        return false
      }
      uploadFilesBus.$emit('documentRequired', false)
      this.$validator
        .validate()
        .then((result) => {
          if (result) {
            const form = e.target.elements
            const formData = {
              exporter_user_id: this.$store.state.account.accountId,
              importer_user_id: this.buyersValue && this.buyersValue.id,
              remitter_name: form.remitter_name && form.remitter_name.value,
              currency: CURRENCY_MAP[this.currencyValue],
              remitted_amount: this.convertToNumber(
                form.remittance_amount && form.remittance_amount.value
              ),
              linked_invoices:
                this.invoiceValue &&
                this.invoiceValue
                  .filter((inv) => inv.id !== 0)
                  .map((inv) => inv.id),
              unfinanced_amount: this.convertToNumber(
                form.amount && form.amount.value
              ),
              unfinanced_notes: form.narration && form.narration.value,
              unfinanced_client_bank_detail_id:
                this.bankValue && this.bankValue.id,
              document_ids:
                this.uploadsSaved[0].remittance_supporting_document.map(
                  (x) => x.docId
                ),
              notes: form.instructions && form.instructions.value
            }
            this.$emit('addRemittanceAccount', formData)
          } else {
            this.scrollToErrors()
          }
        })
        .catch((e) => {
          this.scrollToErrors()
          console.log(e)
        })
    }
  },
  watch: {
    buyersValue(val) {
      if (val && val.id && this.currencyValue) {
        this.getInvoiceList(val.id, this.currencyValue)
      }
    },
    currencyValue(val) {
      if (val && this.buyersValue && this.buyersValue.id) {
        this.getInvoiceList(this.buyersValue.id, val)
      }
      if (val) {
        this.getBanksList(val)
      }
    },
    isSameName(val) {
      if (val === 'true') {
        if (this.buyersValue) {
          this.remitterName = this.buyersValue.importer
          return
        }
        this.remitterName = ''
      }
      this.remitterName = ''
    }
  }
}
</script>
<style lang="scss">
#currency::placeholder {
  color: $neutral-gray-500;
}
#invoices-0 {
  border-bottom: 3px solid $sky-blue-400;
}
.checkbox-1 {
  .custom-control-label {
    padding-top: 3px;
  }
  .custom-control-label::before,
  .custom-control-label::after {
    width: 1.2rem;
    height: 1.2rem;
    cursor: pointer;
  }
  .custom-control-label::before {
    border: 2px solid $wealthy-green-500;
  }
}
</style>
<style lang="scss" scoped>
.input-container {
  width: 100%;
  margin-bottom: 16px;
  .input-label {
    font-size: 14px;
    font-weight: 400;
    line-height: 19px;
    letter-spacing: 0em;
    text-align: left;
    color: $noble-blue-500;
    margin-bottom: 8px;
    &.font-16 {
      font-size: 16px;
    }
  }
  .input-block {
    display: flex;
    flex-direction: column;
    .prepend-item {
      position: absolute;
      left: 8px;
      top: 16px;
      width: 36px;
      text-align: center;
      font-weight: bold;
      font-size: 16px;
      color: $noble-blue-500;
    }
    .prepend-icon {
      padding-left: 50px;
    }
    .drip-dropdown {
      height: 56px;
      width: 400px;
      @media screen and (max-width: 768px) {
        width: auto;
      }
      .action-input {
        position: absolute;
        cursor: pointer;
        right: 0;
        padding-left: 1rem;
        padding-right: 1rem;
        line-height: 2.6rem;
        font-size: 1.2rem;
        border-bottom-right-radius: 5px;
        border-top-right-radius: 5px;
        top: 1.6rem;
        &.inverse {
          transform: rotate(180deg);
        }
      }
      ::v-deep .multiselect__select {
        height: 100%;
        width: 20px;
        margin-right: 8px;
        padding: 0px;
        &::before {
          z-index: 1;
        }
      }
      ::v-deep .multiselect__tags {
        outline: 0px;
        height: 56px;
        padding: 16px;
        padding-right: 36px;
        border-radius: 8px;
        border: 1px solid $noble-blue-500;
        color: $noble-blue-500;
        margin-bottom: 4px;
        .multiselect__placeholder {
          color: $neutral-gray-500;
          margin-top: -2px;
          padding-top: 0px;
          padding-left: 5px;
          font-size: 16px;
        }
        .multiselect__single {
          color: $noble-blue-500;
          font-weight: 700;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          vertical-align: bottom;
          width: 90% !important;
        }
      }
      ::v-deep .multiselect__option {
        background-color: white;
        color: $noble-blue-500;
      }
      ::v-deep .multiselect__option--highlight {
        background-color: white;
        color: $noble-blue-500;
      }
      ::v-deep .multiselect__option--selected {
        background-color: white;
      }
      ::v-deep .multiselect__option:hover {
        background-color: $sky-blue-50;
        color: $wealthy-green-600;
      }
      ::v-deep .multiselect__content-wrapper {
        border: 1px solid $noble-blue-50;
        box-shadow: 0px 6px 12px 0px #0a2e5733;
      }
    }
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px white inset !important;
    }
    input {
      outline: 0px;
      width: 400px;
      @media screen and (max-width: 768px) {
        width: auto;
      }
      height: 56px;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid $noble-blue-500;
      color: $noble-blue-500;
      font-size: 16px;
      font-weight: 700;
      &::placeholder {
        font-weight: 400;
        color: $neutral-gray-500;
      }
      &.width-800 {
        width: 808px;
      }
    }
    textarea {
      outline: 0px;
      width: 100%;
      height: 56px;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid $sky-blue-400;
      color: $noble-blue-500;
      font-size: 16px;
      font-weight: 400;
      &::placeholder {
        font-weight: 400;
        color: $neutral-gray-500;
      }
      &.width-800 {
        width: 808px;
      }
    }
    .error {
      color: $red-400;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: left;
      margin-top: 8px;
      @media screen and (max-width: 768px) {
        margin-top: 2px;
      }
    }
  }
  .supporting-document {
    display: flex;
    flex-direction: column;
    .error {
      color: $red-400;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: left;
      margin-top: 8px;
      @media screen and (max-width: 768px) {
        margin-top: 2px;
      }
    }
  }
}
.bottomBorder {
  border-bottom: 1px solid $sky-blue-400;
}
</style>
